'use client'

import { showsCustomerClosure } from '@/core/utils/support-presentation'
import { observer } from 'mobx-react-lite'
import { Package, Tag } from 'lucide-react'
import { AvatarCircle } from './avatar-circle'
import { Conversation } from '@/core/repositories/conversations'
import { AppStore } from '@/core/store'
import {
  isListingReference,
  parseMessageReference,
  referencePreview,
  referenceTitle,
  stripReferenceSummary,
} from '@/core/utils/message-references'

function fmtRelTime(s: string): string {
  const ms = Date.now() - new Date(s).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'now'
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d`
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function otherParticipant(c: Conversation, viewerId: string | null) {
  if (!c.participants || c.participants.length === 0) return null
  const other = c.participants.find((p) => p.userId && p.userId !== viewerId)
  return other ?? c.participants[0]
}

function rowTitle(c: Conversation, viewerId: string | null): string {
  if (c.type === 'support') {
    // Support conversations created via /console/support/compose carry an
    // operator-set subject in metadata. Prefer that — it's much more useful
    // as a row label than a generic "MarkitMix Support".
    const s = (c.metadata as { subject?: unknown } | null | undefined)?.subject
    if (typeof s === 'string' && s.trim()) return s.trim()
    return 'MarkitMix Support'
  }
  const other = otherParticipant(c, viewerId)
  const u = other?.user
  if (u?.displayName) return u.displayName
  if (u?.name) return u.name
  if (u?.email) return u.email.split('@')[0]
  return 'Conversation'
}

/**
 * Registered (legal) name shown in parens after the handle in a row, mirroring
 * the thread header. Only when the viewer is the SELLER in this peer thread —
 * so a seller can match a row to an order. A buyer never sees a seller's legal
 * name (the backend `applyParticipantNameVisibility` also gates this, so the
 * legal name isn't even in the buyer's list payload). Skipped when it matches
 * the display name (no redundant parens). Returns null when nothing to show.
 */
function rowRegisteredName(c: Conversation, viewerId: string | null): string | null {
  if (c.type === 'support') return null
  const viewerRole = c.participants?.find((p) => p.userId === viewerId)?.role
  if (viewerRole !== 'seller') return null
  const u = otherParticipant(c, viewerId)?.user
  const displayName = u?.displayName?.trim() || ''
  const registered = u?.name?.trim() || ''
  if (!displayName || !registered) return null
  if (registered.toLowerCase() === displayName.toLowerCase()) return null
  return registered
}

function rowSubtitle(c: Conversation, viewerId: string | null): string {
  // Prefer the actual most-recent message preview (matches every modern
  // chat app — WhatsApp / Messenger / iMessage). Falls back to context
  // descriptors only when there isn't a usable message body.
  const last = c.messages?.[c.messages.length - 1]
  if (last) {
    const sentByMe = !!last.senderUserId && last.senderUserId === viewerId
    const prefix = sentByMe ? 'You: ' : ''
    // Auto-replies preview in the same third-person voice as their notice in
    // the thread — never as words the sender typed (so no "You:" either).
    const arMeta = (last.metadata as any) || {}
    if (arMeta.vacationAutoReply && typeof arMeta.awayUntilLabel === 'string') {
      const name =
        last.sender?.displayName || last.sender?.name || 'The seller'
      return `${name} is away until ${arMeta.awayUntilLabel}`
    }
    // A message sent with a listing / order card carries the card's summary
    // in its body for older clients; the preview shows what was typed, or
    // names the card when nothing was.
    const reference = parseMessageReference(last.metadata)
    const typed = stripReferenceSummary(last.body, reference).trim()
    if (typed) {
      const truncated = typed.length > 80 ? typed.slice(0, 80) + '…' : typed
      return `${prefix}${truncated}`
    }
    if (reference) return `${prefix}${referencePreview(reference)}`
    // Attachment-only message — surface the attachment type so the row
    // preview reads naturally ("You: 📷 Photo", "Jenny: 🎬 Video").
    const attachments = last.attachments || []
    if (attachments.length > 0) {
      const first = attachments[0]
      const mime = first.mime || ''
      let descriptor = 'Attachment'
      if (mime.startsWith('image/')) {
        descriptor = attachments.length > 1 ? `${attachments.length} photos` : 'Photo'
      } else if (mime.startsWith('video/')) {
        descriptor = attachments.length > 1 ? `${attachments.length} videos` : 'Video'
      } else if (attachments.length > 1) {
        descriptor = `${attachments.length} files`
      }
      return `${prefix}${descriptor}`
    }
  }
  if (c.category === 'inquiry') return 'Question'
  if (c.category === 'bug_report') return 'Bug report'
  if (c.category === 'suggestion') return 'Suggestion'
  return ''
}

/**
 * Derive the role-context badge for a row when we're rendering in a
 * unified surface (the FAB quick-glance panel — tabs aren't shown there
 * because there's no room and it's a "what's new across everything" view).
 * Returns null when no badge should render (support thread, missing role).
 */
function rowRoleBadge(
  c: Conversation,
  viewerId: string | null
): { label: string; color: string } | null {
  if (c.type === 'support') {
    return { label: 'Support', color: 'var(--primary)' }
  }
  if (c.type !== 'peer') return null
  // A peer thread pinned to a listing with no order behind it is a pre-purchase
  // question, not a sale/purchase. Only order-backed threads carry buy/sell.
  const isOrderBacked = !!c.contextOrderId
  if (!isOrderBacked) {
    return { label: 'Question', color: 'var(--ios-blue)' }
  }
  const myRole = c.participants?.find((p) => p.userId === viewerId)?.role
  if (myRole === 'seller') {
    return { label: 'Sale', color: 'var(--ios-green)' }
  }
  if (myRole === 'buyer') {
    return { label: 'Purchase', color: 'var(--ios-blue)' }
  }
  return null
}

function avatarInitial(c: Conversation, title: string): string {
  if (c.type === 'support') return 'M'
  return title.trim()[0]?.toUpperCase() || '?'
}

const AVATAR_PALETTE = [
  'var(--ios-blue)',
  'var(--ios-green)',
  'var(--ios-orange)',
  'var(--ios-purple, #af52de)',
  'var(--ios-pink, #ff2d55)',
  'var(--ios-teal, #5ac8fa)',
  'var(--ios-indigo, #5856d6)',
]
function avatarColor(c: Conversation, title: string): string {
  if (c.type === 'support') return 'var(--primary)'
  const ch = (title.trim()[0]?.toUpperCase() || '?').charCodeAt(0)
  return AVATAR_PALETTE[ch % AVATAR_PALETTE.length]
}

function avatarUrl(c: Conversation, viewerId: string | null): string | null {
  if (c.type === 'support') return null
  const other = otherParticipant(c, viewerId)
  const path = other?.user?.profileImagePath
  if (!path) return null
  if (path.startsWith('http')) return path
  const base = process.env.NEXT_PUBLIC_SERVER_URL || ''
  return `${base}/${path.startsWith('assets/') ? path : `assets/${path}`}`
}

interface Props {
  conversation: Conversation
  selected?: boolean
  /** When provided, row triggers in-place selection. Otherwise it falls back
   *  to a real anchor link to /inbox/<id>. */
  onSelect?: () => void
  /** Avatar-only rail variant (used in mobile sidebar). */
  compact?: boolean
  /** Render a small inline role chip (Sale / Purchase / Support) before the
   *  message preview. Used by unified surfaces (the FAB quick-glance panel)
   *  that don't have tabs to convey role context. Skipped by /inbox where
   *  the active tab already implies the role. */
  showRoleBadge?: boolean
}

export const ConversationListRow = observer(
  ({ conversation, selected, onSelect, compact, showRoleBadge }: Props) => {
  const c = conversation
  const viewerId = AppStore.accountData?.id ?? null

  const myParticipant = viewerId
    ? c.participants?.find((p) => p.userId === viewerId)
    : c.participants?.[0]
  const myUnread = myParticipant?.unreadCount ?? 0
  const isUnread = myUnread > 0

  const title = rowTitle(c, viewerId)
  const registeredName = rowRegisteredName(c, viewerId)
  const subtitle = rowSubtitle(c, viewerId)
  const latestReference = c.type === 'peer' ? c.latestReference ?? null : null
  const roleBadge = showRoleBadge ? rowRoleBadge(c, viewerId) : null
  const avUrl = avatarUrl(c, viewerId)
  const isClosed = showsCustomerClosure(c)
  const closedBadge = isClosed
    ? { label: c.type === 'support' ? 'Conversation closed' : 'Closed' }
    : null

  // Online presence: existing socket already tracks connected userIds in
  // AppStore.loggedInAccounts. Surface a green dot when the OTHER party of
  // a peer thread is currently connected. Support threads (talking to the
  // platform) never show a presence dot.
  const otherPeer = otherParticipant(c, viewerId)
  const otherIsOnline =
    c.type === 'peer' &&
    !!otherPeer?.userId &&
    AppStore.loggedInAccounts.includes(otherPeer.userId)

  // ── Compact rail (mobile sidebar) ────────────────────────────────────
  if (compact) {
    const inner = (
      <div
        title={title}
        aria-label={title}
        style={{
          // Outer wrapper has NO overflow: hidden — badges/dots positioned
          // outside the avatar would otherwise be clipped at the circle edge.
          position: 'relative',
          width: 44,
          height: 44,
          flexShrink: 0,
        }}
      >
        {/* Inner clipped avatar — this is what gets the circle mask */}
        <AvatarCircle
          url={avUrl}
          initial={avatarInitial(c, title)}
          bgColor={avatarColor(c, title)}
          isSupport={c.type === 'support'}
          size="100%"
          style={{
            // Selected ring uses box-shadow to avoid layout shift.
            boxShadow: selected ? '0 0 0 2px var(--primary)' : 'none',
            transition: 'box-shadow 120ms',
          }}
        />
        {isUnread && (
          <span
            aria-label={`${myUnread} unread`}
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: 'var(--ios-red)',
              color: 'white',
              fontSize: 12,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow:
                '0 0 0 2px var(--background_2, white)',
              lineHeight: 1,
            }}
          >
            {myUnread > 9 ? '9+' : myUnread}
          </span>
        )}
        {otherIsOnline && (
          <span
            aria-label="Active now"
            title="Active now"
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 11,
              height: 11,
              borderRadius: '50%',
              background: 'var(--ios-green)',
              boxShadow: '0 0 0 2px var(--background_2, white)',
            }}
          />
        )}
      </div>
    )

    if (onSelect) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onSelect()
            }
          }}
          className="cursor-pointer select-none"
          style={{
            padding: '2px 0',
            display: 'inline-flex',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            fontFamily: 'inherit',
          }}
        >
          {inner}
        </div>
      )
    }
    return (
      <a
        href={`/inbox/${c.id}`}
        style={{
          padding: '2px 0',
          display: 'inline-flex',
          justifyContent: 'center',
          textDecoration: 'none',
        }}
      >
        {inner}
      </a>
    )
  }
  // ── Full row ─────────────────────────────────────────────────────────

  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 10,
    textDecoration: 'none' as const,
    color: 'inherit',
    width: '100%',
    boxSizing: 'border-box' as const,
    background: selected
      ? 'color-mix(in srgb, var(--primary) 14%, transparent)'
      : 'transparent',
    transition: 'background 120ms',
    fontFamily: 'inherit' as const,
  } as const

  const handleHover = (e: React.MouseEvent<HTMLElement>, on: boolean) => {
    if (selected) return
    ;(e.currentTarget as HTMLElement).style.background = on
      ? 'color-mix(in srgb, var(--font_1) 5%, transparent)'
      : 'transparent'
  }

  const Inner = (
    <>
      {/* Avatar — perfectly round, consistent with the rest of the platform */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <AvatarCircle
          url={avUrl}
          initial={avatarInitial(c, title)}
          bgColor={avatarColor(c, title)}
          isSupport={c.type === 'support'}
          size={40}
        />
        {otherIsOnline && (
          <span
            aria-label="Active now"
            title="Active now"
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 11,
              height: 11,
              borderRadius: '50%',
              background: 'var(--ios-green)',
              boxShadow: '0 0 0 2px var(--background_2, white)',
            }}
          />
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 6,
          }}
        >
          <span
            style={{
              fontSize: 14,
              fontWeight: isUnread ? 600 : 500,
              // Closed threads mute slightly so an active chat still wins
              // at a glance, but the title stays legible.
              color: isClosed ? 'var(--font_2)' : 'var(--font_1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.01em',
            }}
          >
            {title}
            {registeredName && (
              <span style={{ fontWeight: 400, color: 'var(--font_3)' }}> ({registeredName})</span>
            )}
          </span>
          <span
            style={{
              fontSize: 12,
              color: isUnread ? 'var(--primary)' : 'var(--font_3)',
              flexShrink: 0,
              fontWeight: isUnread ? 600 : 400,
            }}
          >
            {fmtRelTime(c.lastMessageAt)}
          </span>
        </div>
        {latestReference && (
          // What this thread is about now: the newest listing / order card in
          // its messages. Not the conversation's own context fields, which
          // only remember the first item this pair ever discussed.
          <div
            title={referenceTitle(latestReference)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 2,
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--font_3)',
              minWidth: 0,
            }}
          >
            {isListingReference(latestReference) ? (
              <Tag size={12} style={{ flexShrink: 0 }} />
            ) : (
              <Package size={12} style={{ flexShrink: 0 }} />
            )}
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {referenceTitle(latestReference)}
            </span>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
            marginTop: 2,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              color: isUnread ? 'var(--font_1)' : 'var(--font_2)',
              fontWeight: isUnread ? 500 : 400,
            }}
          >
            {closedBadge ? (
              // Per the project's "no colored-theme" rule, status badges use
              // neutral chrome — the label itself conveys meaning. Title row
              // is already muted when closed; this just gives the state a
              // small inline name without the AI-flavored green tint.
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--font_2)',
                  background: 'var(--background_2)',
                  border: '1px solid var(--separator)',
                  padding: '1px 6px',
                  borderRadius: 4,
                  letterSpacing: '0.02em',
                }}
              >
                {closedBadge.label}
              </span>
            ) : roleBadge ? (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: 600,
                  color: roleBadge.color,
                  background: `color-mix(in srgb, ${roleBadge.color} 14%, transparent)`,
                  padding: '1px 6px',
                  borderRadius: 4,
                  letterSpacing: '0.02em',
                }}
              >
                {roleBadge.label}
              </span>
            ) : null}
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {subtitle}
            </span>
          </span>
          {isUnread && (
            <span
              style={{
                background: 'var(--primary)',
                color: 'var(--primary_text)',
                fontSize: 12,
                fontWeight: 600,
                padding: '0 6px',
                height: 18,
                minWidth: 18,
                borderRadius: 999,
                textAlign: 'center',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {myUnread}
            </span>
          )}
        </div>
      </div>
    </>
  )

  if (onSelect) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
        className="cursor-pointer select-none"
        style={baseStyle}
      >
        {Inner}
      </div>
    )
  }
  return (
    <a
      href={`/inbox/${c.id}`}
      onMouseEnter={(e) => handleHover(e, true)}
      onMouseLeave={(e) => handleHover(e, false)}
      style={baseStyle}
    >
      {Inner}
    </a>
  )
})
